import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormFileField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CreatePromoMaterialForLandingPage } from 'api/ClientArea/CreatePromoMaterials'
import { LandingPageDto } from 'api/ClientArea/LandingPageDto'
import { PreviewPromoMaterialLandingPage } from 'api/ClientArea/PreviewPromoMaterials'
import { PromoMaterialLandingPageDto } from 'api/ClientArea/PromoMaterialDto'
import { PromoMaterialsOptionsDto } from 'api/ClientArea/PromoMaterialsOptionsDto'
import { UpdatePromoMaterialLandingPage } from 'api/ClientArea/UpdatePromoMaterial'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchPromoMaterialsOptionsAction } from 'store/clientarea/clientarea.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { convertDateToUTC, createDateAsUTC } from 'utils/date'
import { toBase64 } from 'utils/file.helpers'
import { FormSubmitValues } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'

import { PermissionDto } from '../../../api/Identity/Types/PermissionDto'
import { PermissionsEnum } from '../../../api/Identity/Types/PermissionsEnum'
import { LoadingOverlay } from '../../../components/LoadingOverlay/LoadingOverlay'
import { hasPermission } from '../../../utils/permissionsValidator'

export interface PromoMaterialFormValues {
  name?: string
  image?: string
  fileImage?: string
  promoMaterialCategoryId?: number
  promoMaterialTypeId?: number
  languageId?: string
  languageName?: string
  active?: boolean
  fileSize?: string
  endDateTime?: string
}

const useMapStateToProps = () => ({
  promoMaterialsOptions: useAppSelector((state) => state.clientArea.promoMaterialsOptions),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  return {
    fetchPromoMaterialsOptions: async () =>
      dispatch(fetchPromoMaterialsOptionsAction(clientAreaApiClient)),
  }
}

const usePromoMaterialFormFetch = () => {
  const { promoMaterialsOptions } = useMapStateToProps()
  const { fetchPromoMaterialsOptions } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(promoMaterialsOptions.baseUrls)) {
      fetchPromoMaterialsOptions()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { promoMaterialsOptions }
}

const PromoMaterialFormUI: React.FC<FormikProps<PromoMaterialFormValues> & OuterProps> = (
  props
) => {
  const {
    data,
    values,
    setFieldValue,
    errors,
    isNew = false,
    promoMaterialPreview,
    permissions,
    isSubmitting,
  } = props
  const { languages = [] } = data

  const [isEdit, setEdit] = useState<boolean>(isNew)
  const [isPreviewValidated, setPreviewValidated] = useState<boolean>(!isNew)
  const [isLoading, setLoading] = useState<boolean>(false)
  const languageOptions = useMemo(
    () => (languages || []).map((language) => ({ id: language.id, name: language.name })),
    [languages]
  )

  const { promoMaterialsOptions } = usePromoMaterialFormFetch()

  const handlePromoMaterialValidate = async () => {
    if (isFormDataFilled(values)) {
      try {
        setLoading(true)
        const newData = dataToSubmit(props)(values)
        if (newData && props?.onValidate) {
          await props.onValidate([newData])
          setPreviewValidated(true)
        }
      } catch (e) {
        console.error(e)
        setPreviewValidated(false)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleUploadImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files || []
    if (files.length === 0) {
      console.error('No file selected.')
      setFieldValue('fileImage', undefined)
      setFieldValue('fileSize', undefined)
      return
    }

    try {
      const [file] = files
      const imageElement = await handleFileUpload(file)
      if (imageElement) {
        setFieldValue('fileSize', `${imageElement.width}x${imageElement.height}`)
        setFieldValue('fileImage', imageElement.src)
      }
    } catch (error) {
      if (error instanceof Error && error.message) {
        console.error('Error reading file:', error.message)
      }
    }
  }

  return (
    <Form>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add new Promo Material' : 'Edit Promo Material'}</DialogTitle>
        </DialogHeader>

        <FormField
          name='name'
          error={errors.name}
          label='Name'
          readOnly={!isEdit}
          value={values.name}
        />

        {!isNew && (
          <FormReadonlyField name='image' label='Current Image Path' value={values.image} />
        )}

        {isNew && isEdit && (
          <Tooltip>
            <TooltipTrigger>
              <FormFileField
                accept='image/jpeg'
                onChange={handleUploadImage}
                name='fileImage'
                label='Image'
              />
            </TooltipTrigger>
            <TooltipContent side='top'>
              Please upload an image with one of the specified sizes: <br />
              {promoMaterialsOptions.sizes.map((x) => `${x.width}x${x.height}`).join(', ')}.
            </TooltipContent>
          </Tooltip>
        )}

        {isPreviewValidated && promoMaterialPreview && (
          <FormReadonlyField
            name='promoMaterialPreview.name'
            label='Size ID'
            value={promoMaterialPreview.promoMaterialSize?.name}
          />
        )}

        {isPreviewValidated && promoMaterialPreview && (
          <FormReadonlyField
            name='promoMaterialPreview.category'
            label='Promo Material Category'
            value={promoMaterialPreview.promoMaterialCategory.name}
          />
        )}

        {isPreviewValidated && promoMaterialPreview && (
          <FormReadonlyField
            name='promoMaterialType.name'
            label='Promo Material Type'
            value={promoMaterialPreview.promoMaterialType?.name}
          />
        )}

        <FormSelectField
          name='languageName'
          label='Language'
          error={errors.languageId}
          value={values.languageId}
          options={languageOptions}
          readOnly={!isEdit}
          showSearch
          allowClear
          onChange={(value) => setFieldValue('languageId', value)}
        />

        <FormDateField
          error={errors.endDateTime}
          name='endDateTime'
          label='End Date'
          readOnly={!isEdit}
        />

        <FormCheckboxField error={errors.active} name='active' label='Active' readOnly={!isEdit} />

        <DialogFooter>
          <ActionButtons
            {...props}
            isEdit={isEdit}
            setEdit={setEdit}
            isPreviewValidated={isPreviewValidated}
            setPreviewValidated={setPreviewValidated}
            onPromoMaterialValidate={handlePromoMaterialValidate}
            permissions={permissions}
          />
        </DialogFooter>
        <LoadingOverlay isLoading={isSubmitting || isLoading} />
      </DialogContent>
    </Form>
  )
}

export type CreatePromoMaterialValues = Omit<CreatePromoMaterialForLandingPage, 'id'>
export type UpdatePromoMaterialValues = UpdatePromoMaterialLandingPage & { id: string }

export const dataToSubmit =
  (props: OuterProps) =>
  (
    values: Required<PromoMaterialFormValues>
  ): CreatePromoMaterialValues | UpdatePromoMaterialValues => {
    const { promoMaterial } = props

    if (promoMaterial?.id) {
      return {
        id: promoMaterial.id,
        name: values.name || promoMaterial.name || null,
        landingPageId: promoMaterial.landingPage?.id || null,
        promoMaterialSizeId: promoMaterial.promoMaterialSize.id,
        promoMaterialCategoryId:
          values?.promoMaterialCategoryId || promoMaterial.promoMaterialCategory.id,
        promoMaterialTypeId: promoMaterial.promoMaterialType.id,
        tickmillCompanyId: promoMaterial.tickmillCompany.id,
        languageId: values?.languageId || promoMaterial.language.id,
        enabled: values.active ?? promoMaterial.enabled ?? false,
        endDateTime: values?.endDateTime
          ? createDateAsUTC(values.endDateTime, false, true).toISOString()
          : null,
      }
    }

    return {
      name: values.name,
      promoMaterial: values.fileImage,
      languageId: values.languageId,
      endDateTime: values?.endDateTime
        ? createDateAsUTC(values.endDateTime, false, true).toISOString()
        : null,
      isEnabled: values?.active ?? false,
    }
  }

interface OuterProps {
  data: LandingPageDto
  promoMaterial?: PromoMaterialLandingPageDto
  promoMaterialsOptions: PromoMaterialsOptionsDto
  promoMaterialPreview?: PreviewPromoMaterialLandingPage
  permissions?: PermissionDto[]
  onSubmit(
    values: FormSubmitValues<CreatePromoMaterialValues | UpdatePromoMaterialValues>
  ): Promise<void>
  onValidate?(
    values: FormSubmitValues<(CreatePromoMaterialValues | UpdatePromoMaterialValues)[]>
  ): Promise<PreviewPromoMaterialLandingPage[] | undefined>
  isNew?: boolean
}

export const PromoMaterialForm = withFormik<OuterProps, PromoMaterialFormValues>({
  mapPropsToValues: (props) => {
    const { promoMaterial } = props
    const [landingPromoMaterial] = promoMaterial?.landingPage?.promoMaterials || []

    return {
      name: landingPromoMaterial?.name ?? undefined,
      image: landingPromoMaterial?.imagePath ?? undefined,
      fileImage: landingPromoMaterial?.imagePath ?? undefined,
      promoMaterialCategoryId: landingPromoMaterial?.promoMaterialCategory?.id ?? undefined,
      promoMaterialTypeId: undefined,
      languageId: landingPromoMaterial?.language?.id ?? undefined,
      languageName: landingPromoMaterial?.language?.name ?? undefined,
      endDateTime: promoMaterial?.endDateTime
        ? convertDateToUTC(promoMaterial.endDateTime).toISOString()
        : undefined,
      active: landingPromoMaterial?.enabled ?? true,
      fileSize: undefined,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      if (isFormDataFilled(values)) {
        const data = dataToSubmit(props)(values)
        await props.onSubmit(data)
      }
    } finally {
      setSubmitting(false)
    }
  },
  validate: async (values) => {
    const errors: FormikErrors<PromoMaterialFormValues> = {}

    if (!values.name) {
      errors.name = 'Required'
    }

    if (!values.fileImage) {
      errors.fileImage = 'Required'
    }

    if (!values.languageId) {
      errors.languageId = 'Required'
    }

    return errors
  },
  enableReinitialize: false,
  validateOnChange: true,
})(PromoMaterialFormUI)

interface ActionProps extends FormikProps<PromoMaterialFormValues>, OuterProps {
  isEdit: boolean
  setEdit(value: boolean): void
  isPreviewValidated: boolean
  setPreviewValidated(value: boolean): void
  onPromoMaterialValidate(): void
  permissions?: PermissionDto[]
}

const ActionButtons = (props: ActionProps) => {
  const { isNew } = props

  if (isNew) {
    return <ActionNewButtons {...props} />
  }

  return <ActionEditButtons {...props} />
}

const ActionNewButtons = (props: ActionProps) => {
  const { isValid, isSubmitting, handleSubmit, isPreviewValidated, onPromoMaterialValidate } = props

  const handleSave = () => {
    handleSubmit()
  }

  if (isPreviewValidated) {
    return (
      <ButtonGroup>
        <Button
          type='button'
          disabled={!isValid}
          key='button'
          onClick={handleSave}
          data-test='save-button'
        >
          Save
        </Button>
      </ButtonGroup>
    )
  }

  return (
    <ButtonGroup>
      <Button
        type='button'
        disabled={!isValid || isSubmitting}
        key='button'
        onClick={onPromoMaterialValidate}
        data-test='validate-button'
      >
        Validate
      </Button>
    </ButtonGroup>
  )
}

const ActionEditButtons = (props: ActionProps) => {
  const { isEdit, setEdit, isValid, isSubmitting, resetForm, submitForm, permissions } = props

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleSave = () => {
    setEdit(false)
    submitForm()
  }

  return (
    <ButtonGroup>
      {!isEdit ? (
        <Button
          onClick={() => setEdit(true)}
          data-test='edit-button'
          disabled={!hasPermission(permissions, PermissionsEnum.CanEditPromoMaterial)}
        >
          Edit
        </Button>
      ) : (
        <React.Fragment>
          <Button
            variant='outline'
            className='is-danger'
            onClick={handleCancel}
            data-test='cancel-button'
          >
            Cancel
          </Button>
          <Button
            disabled={!isValid || isSubmitting}
            key='button'
            type='button'
            onClick={handleSave}
            data-test='save-button'
          >
            Save
          </Button>
        </React.Fragment>
      )}
    </ButtonGroup>
  )
}

const isFormDataFilled = (
  values: PromoMaterialFormValues
): values is Required<PromoMaterialFormValues> => {
  const requiredFields: (keyof PromoMaterialFormValues)[] = ['name', 'fileImage']
  return requiredFields.every((field) => values[field] !== undefined)
}

const handleFileUpload = async (file: File): Promise<HTMLImageElement> => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    const base64 = await toBase64(file)
    if (base64) {
      const image = await getImage(base64)
      resolve(image)
    } else {
      reject()
    }
  })
}

const getImage = async (base64?: string | ArrayBuffer): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const newBase64 = typeof base64 === 'string' ? base64 : ''

    img.onload = function () {
      img.src = newBase64 as string
      resolve(this as HTMLImageElement)
    }

    img.onerror = function (error) {
      reject(error)
    }

    img.src = newBase64 as string
  })
}
